import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { SelectButton } from 'primereact/selectbutton';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import type { ApplicationSdk } from '../../../core/sdk';
import MenuManagementStore, { MenuItem } from '../stores/MenuManagementStore';
import './MenuManagement.css';

const MenuManagement = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const [store] = useState(() => new MenuManagementStore(sdk));

    useEffect(() => {
        store.init();
    }, [store]);

    const menuTypeOptions = [
        { label: 'Route', value: 'route' },
        { label: 'IFrame', value: 'iframe' },
        { label: 'External', value: 'external' },
    ];

    const iconOptions = [
        { label: 'Circle', value: 'pi pi-circle' },
        { label: 'Home', value: 'pi pi-home' },
        { label: 'User', value: 'pi pi-user' },
        { label: 'Users', value: 'pi pi-users' },
        { label: 'Cog', value: 'pi pi-cog' },
        { label: 'Box', value: 'pi pi-box' },
        { label: 'Shop', value: 'pi pi-shop' },
        { label: 'Truck', value: 'pi pi-truck' },
        { label: 'Book', value: 'pi pi-book' },
        { label: 'Briefcase', value: 'pi pi-briefcase' },
        { label: 'Star', value: 'pi pi-star' },
        { label: 'Plus', value: 'pi pi-plus' },
        { label: 'Minus', value: 'pi pi-minus' },
        { label: 'Check', value: 'pi pi-check' },
        { label: 'Times', value: 'pi pi-times' },
        { label: 'Calendar', value: 'pi pi-calendar' },
        { label: 'Clock', value: 'pi pi-clock' },
        { label: 'Map', value: 'pi pi-map' },
        { label: 'Chart', value: 'pi pi-chart-bar' },
        { label: 'Database', value: 'pi pi-database' },
    ];

    const confirmDelete = (item: MenuItem) => {
        confirmDialog({
            message: `Möchten Sie den Menü-Eintrag "${item.label}" wirklich löschen?`,
            header: 'Löschen bestätigen',
            icon: 'pi pi-exclamation-triangle',
            accept: () => store.deleteMenuItem(item),
            acceptLabel: 'Ja, löschen',
            rejectLabel: 'Abbrechen',
            acceptClassName: 'p-button-danger',
        });
    };

    // Handle drag and drop reordering
    const handleRowReorder = (e: any) => {
        const reorderedItems = e.value.map((item: MenuItem, index: number) => ({
            ...item,
            sort_order: index,
        }));
        store.reorderMenuItems(reorderedItems);
    };

    const actionBodyTemplate = (rowData: MenuItem) => {
        return (
            <div className="flex gap-1">
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    size="small"
                    onClick={() => store.openEditDialog(rowData)}
                    tooltip="Bearbeiten"
                />
                <Button
                    icon="pi pi-trash"
                    rounded
                    text
                    size="small"
                    severity="danger"
                    onClick={() => confirmDelete(rowData)}
                    tooltip="Löschen"
                />
            </div>
        );
    };

    const iconBodyTemplate = (rowData: MenuItem) => {
        return (
            <div className="flex align-items-center gap-2">
                <i className={rowData.icon}></i>
                <span>{rowData.icon}</span>
            </div>
        );
    };

    const activeBodyTemplate = (rowData: MenuItem) => {
        return (
            <i
                className={rowData.active ? 'pi pi-check' : 'pi pi-times'}
                style={{
                    color: rowData.active ? '#22c55e' : '#ef4444',
                    fontSize: '1rem',
                }}
            ></i>
        );
    };

    const typeBodyTemplate = (rowData: MenuItem) => {
        const typeLabels = {
            route: 'Route',
            iframe: 'IFrame',
            external: 'External',
        };
        return typeLabels[rowData.type] || rowData.type;
    };

    // Template for permission column
    const permissionBodyTemplate = (rowData: MenuItem) => {
        const permission = store.availablePermissions.find(
            p => p.id === rowData.required_permission
        );
        return permission ? (
            <Tag
                value={permission.name}
                severity="info"
            />
        ) : (
            <Tag
                value="Keine"
                severity="secondary"
            />
        );
    };

    const renderMenuItemForm = () => (
        <div className="p-fluid">
            <div className="field">
                <label htmlFor="key">Schlüssel</label>
                <InputText
                    id="key"
                    value={store.formData.key}
                    onChange={e => store.setFormData('key', e.target.value)}
                    placeholder="Eindeutiger Schlüssel"
                />
            </div>

            <div className="field">
                <label htmlFor="label">Label</label>
                <InputText
                    id="label"
                    value={store.formData.label}
                    onChange={e => store.setFormData('label', e.target.value)}
                    placeholder="Anzeigename"
                />
            </div>

            <div className="field">
                <label htmlFor="icon">Icon</label>
                <Dropdown
                    id="icon"
                    value={store.formData.icon}
                    options={iconOptions}
                    onChange={e => store.setFormData('icon', e.value)}
                    placeholder="Icon auswählen"
                    optionLabel="label"
                    optionValue="value"
                    itemTemplate={option => (
                        <div className="flex align-items-center gap-2">
                            <i className={option.value}></i>
                            <span>{option.label}</span>
                        </div>
                    )}
                    valueTemplate={option =>
                        option ? (
                            <div className="flex align-items-center gap-2">
                                <i className={option.value || option}></i>
                                <span>{option.label || store.formData.icon}</span>
                            </div>
                        ) : null
                    }
                />
            </div>

            <div className="field">
                <label htmlFor="path">Pfad</label>
                <InputText
                    id="path"
                    value={store.formData.path}
                    onChange={e => store.setFormData('path', e.target.value)}
                    placeholder="/manager/beispiel"
                />
            </div>

            <div className="field">
                <label htmlFor="type">Typ</label>
                <SelectButton
                    id="type"
                    value={store.formData.type}
                    onChange={e => store.setFormData('type', e.value)}
                    options={menuTypeOptions}
                    optionLabel="label"
                    optionValue="value"
                />
            </div>
            {store.formData.type !== 'route' && (
                <div className="field">
                    <label htmlFor="config">Konfiguration</label>
                    <InputTextarea
                        id="config"
                        value={store.formData.config || ''}
                        onChange={(e: any) => store.setFormData('config', e.target.value)}
                        placeholder={
                            store.formData.type === 'iframe'
                                ? `JSON-Konfiguration für iframe (optional):\n{\n  "height": "800px",\n  "allow": "accelerometer; autoplay",\n  "sandbox": "allow-same-origin allow-scripts"\n}`
                                : 'JSON-Konfiguration (optional)'
                        }
                        rows={store.formData.type === 'iframe' ? 5 : 3}
                    />
                    {store.formData.type === 'iframe' && (
                        <small className="p-text-secondary block mt-1">
                            <strong>Pfad:</strong> URL der zu ladenden Seite
                            <br />
                            <strong>Config:</strong> JSON mit iframe-Optionen (height, allow,
                            sandbox, etc.)
                        </small>
                    )}
                </div>
            )}
            <div className="formgrid grid">
                <div className="field col">
                    <label htmlFor="prio">Priorität</label>
                    <InputNumber
                        id="prio"
                        value={store.formData.prio}
                        onChange={e => store.setFormData('prio', e.value || 0)}
                        min={0}
                        max={255}
                    />
                </div>
                <div className="field col">
                    <label htmlFor="sort_order">Sortierreihenfolge</label>
                    <InputNumber
                        id="sort_order"
                        value={store.formData.sort_order}
                        onChange={e => store.setFormData('sort_order', e.value || 0)}
                        min={0}
                    />
                </div>
            </div>
            <div className="field">
                <label htmlFor="required_permission">Erforderliche Berechtigung</label>
                <Dropdown
                    id="required_permission"
                    value={store.formData.required_permission}
                    options={[
                        { label: 'Keine Berechtigung erforderlich', value: null },
                        ...store.availablePermissions.map(permission => ({
                            label: permission.name,
                            value: permission.id,
                        })),
                    ]}
                    onChange={e => store.setFormData('required_permission', e.value)}
                    placeholder="Berechtigung auswählen"
                    optionLabel="label"
                    optionValue="value"
                    showClear
                />
            </div>

            <div className="field">
                <label
                    htmlFor="active"
                    className="flex align-items-center gap-2"
                >
                    <InputSwitch
                        id="active"
                        checked={store.formData.active === 1}
                        onChange={e => store.setFormData('active', e.value ? 1 : 0)}
                    />
                    Aktiv
                </label>
            </div>
        </div>
    );

    if (store.isLoading && store.menuItems.length === 0) {
        return (
            <div
                className="flex justify-content-center align-items-center"
                style={{ height: '200px' }}
            >
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="menu-management">
            <ConfirmDialog />
            <Card
                title="Menü-Verwaltung"
                subTitle="Verwalten Sie die Menü-Einträge der Anwendung"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="flex justify-content-between align-items-center mb-4">
                    <div className="flex align-items-center gap-3">
                        <label htmlFor="menu-select">Menü:</label>
                        <Dropdown
                            id="menu-select"
                            value={store.selectedMenu}
                            options={store.availableMenus.map(menu => ({
                                label: menu,
                                value: menu,
                            }))}
                            onChange={e => store.setSelectedMenu(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            style={{ minWidth: '150px' }}
                        />
                    </div>

                    <div className="flex gap-2">
                        <Button
                            label="Neuer Eintrag"
                            icon="pi pi-plus"
                            onClick={() => store.openCreateDialog()}
                        />
                    </div>
                </div>

                <DataTable
                    value={store.menuItems}
                    loading={store.isLoading}
                    emptyMessage="Keine Menü-Einträge gefunden"
                    stripedRows
                    reorderableRows
                    onRowReorder={e => handleRowReorder(e)}
                    rowClassName={rowData => (rowData.active ? '' : 'menu-item-disabled')}
                >
                    <Column
                        rowReorder
                        style={{ width: '3rem' }}
                    />
                    <Column
                        body={activeBodyTemplate}
                        header="Aktiv"
                        style={{ width: '80px' }}
                    />
                    <Column
                        field="sort_order"
                        header="Reihenfolge"
                        style={{ width: '100px' }}
                    />
                    <Column
                        field="key"
                        header="Schlüssel"
                    />
                    <Column
                        field="label"
                        header="Label"
                    />
                    <Column
                        body={iconBodyTemplate}
                        header="Icon"
                        style={{ width: '200px' }}
                    />
                    <Column
                        field="path"
                        header="Pfad"
                    />
                    <Column
                        body={typeBodyTemplate}
                        header="Typ"
                        style={{ width: '100px' }}
                    />
                    <Column
                        field="prio"
                        header="Priorität"
                        style={{ width: '100px' }}
                    />
                    <Column
                        body={permissionBodyTemplate}
                        header="Berechtigung"
                        style={{ width: '150px' }}
                    />
                    <Column
                        body={actionBodyTemplate}
                        header="Aktionen"
                        style={{ width: '120px' }}
                    />
                </DataTable>
            </Card>

            {/* Create Dialog */}
            <Dialog
                visible={store.isCreateDialogVisible}
                style={{ width: '50rem' }}
                header="Neuen Menü-Eintrag erstellen"
                modal
                onHide={() => store.closeDialogs()}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            outlined
                            onClick={() => store.closeDialogs()}
                        />
                        <Button
                            label="Erstellen"
                            icon="pi pi-check"
                            loading={store.isLoading}
                            onClick={() => store.createMenuItem()}
                        />
                    </div>
                }
            >
                {renderMenuItemForm()}
            </Dialog>

            {/* Edit Dialog */}
            <Dialog
                visible={store.isEditDialogVisible}
                style={{ width: '50rem' }}
                header="Menü-Eintrag bearbeiten"
                modal
                onHide={() => store.closeDialogs()}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Abbrechen"
                            icon="pi pi-times"
                            outlined
                            onClick={() => store.closeDialogs()}
                        />
                        <Button
                            label="Speichern"
                            icon="pi pi-check"
                            loading={store.isLoading}
                            onClick={() => store.updateMenuItem()}
                        />
                    </div>
                }
            >
                {renderMenuItemForm()}
            </Dialog>
        </div>
    );
});

export default MenuManagement;
